import { css } from 'astroturf';
import Prism from 'prismjs';
import React, { useEffect, useMemo, useState } from 'react';

const styles = css`
  .demo {
    background: linear-gradient(135deg, rgba(103, 178, 111, 0.15), rgba(60, 176, 255, 0.15));
    border-radius: 16px;
    box-shadow: rgba(11, 66, 124, 0.2) 0.3em 0.3em 2em inset;
    padding: 1.5rem 2rem;
    margin: 2rem 0;
    font-size: 0.95rem;
  }

  .controls {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1rem;
    align-items: flex-end;
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    flex: 1 1 140px;
    min-width: 120px;
  }

  .field label {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #666;
  }

  .field select,
  .field input,
  .field textarea {
    padding: 0.4rem 0.6rem;
    border: 2px solid #cde;
    border-radius: 6px;
    background: #fff;
    font-family: 'Operator Mono', 'Fira Code', Consolas, monospace;
    font-size: 0.85rem;
    color: #1e1e3f;
    outline: none;
    transition: border-color 0.15s;

    &:focus {
      border-color: dodgerblue;
    }
  }

  .field textarea {
    resize: vertical;
    min-height: 80px;
    font-family: inherit;
    white-space: pre-wrap;
  }

  .output {
    background: #1e1e3f;
    border-radius: 10px;
    padding: 1rem 1rem;
    color: #fff;
    min-height: 3rem;
  }

  .outputLabel {
    font-size: 0.65rem;
    font-weight: 600;
    color: #8888cc;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 0.75rem;
  }

  .comparison {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 1rem;
  }

  .comparisonFullWidth {
    grid-template-columns: 1fr;
  }

  .comparisonItem {
    background: #2a2a5a;
    border-radius: 10px;
    padding: 0.75rem 0.9rem;
    display: flex;
    flex-direction: column;
    gap: 0.55rem;
    min-height: 120px;
  }

  .comparisonHeader {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.5rem;
  }

  .comparisonLocale {
    font-size: 0.65rem;
    color: #8888cc;
    text-transform: uppercase;
  }

  .comparisonStatus {
    font-size: 0.65rem;
    color: #8fd4ff;
  }

  .comparisonValue {
    color: #c3e8b0;
    font-size: 1rem;
    word-break: break-word;
    line-height: 1.4;
  }

  .comparisonValueSmall {
    color: #c3e8b0;
    font-size: 0.9rem;
    line-height: 1.5;
  }

  .comparisonSource {
    color: #dfe7ff;
    font-size: 0.9rem;
    line-height: 1.5;
  }

  .codeExample {
    margin-bottom: 1rem;
  }

  .codeLabel {
    margin-bottom: 0.5rem;
  }

  .codeBlockWrap {
    position: relative;

    & :global(.gatsby-highlight) {
      margin: 0;
      border-radius: 10px;
      overflow: auto;
      background: #171734;
    }

    & :global(pre[class*='language-']) {
      margin: 0;
      padding: 1.5rem;
      font-size: 0.82rem;
      line-height: 1.5;
      white-space: pre;
    }
  }

  .copyButton {
    position: absolute;
    top: 0.65rem;
    right: 0.65rem;
    z-index: 2;
    border: 1px solid rgba(223, 246, 255, 0.18);
    background: rgba(12, 18, 43, 0.92);
    color: #f4fbff;
    border-radius: 999px;
    padding: 0.35rem 0.7rem;
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.28);
    transition:
      background 0.15s,
      border-color 0.15s;

    &:hover {
      background: rgba(22, 31, 71, 0.96);
      border-color: rgba(143, 212, 255, 0.6);
    }
  }

  .tagList {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
  }

  .tag {
    background: #1e1e3f;
    border-radius: 4px;
    padding: 0.2rem 0.5rem;
    font-size: 0.8rem;
    color: #c3e8b0;
    font-family: 'Operator Mono', 'Fira Code', Consolas, monospace;
  }

  .tagWord {
    border: 1px solid dodgerblue;
  }

  .tagSpace {
    color: #999;
    border: 1px dashed #555;
  }

  .tagPunct {
    color: #ff86c6;
    border: 1px solid #ff86c6;
  }

  .note {
    font-size: 0.8rem;
    color: #666;
    margin-top: 0.75rem;
  }

  .legend {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    margin-top: 0.75rem;
    font-size: 0.8rem;
    color: #666;
  }
`;

const LOCALES = [
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'nl-NL', label: 'Dutch' },
  { value: 'fr-FR', label: 'French' },
  { value: 'de-DE', label: 'German' },
  { value: 'es-ES', label: 'Spanish' },
  { value: 'sv-SE', label: 'Swedish' },
  { value: 'ja-JP', label: 'Japanese' },
  { value: 'ar-EG', label: 'Arabic' },
  { value: 'zh-CN', label: 'Chinese' },
  { value: 'ko-KR', label: 'Korean' },
  { value: 'pt-BR', label: 'Portuguese (BR)' },
];

const DEFAULT_COMPARISON_LOCALES = ['en-US', 'nl-NL', 'fr-FR', 'ja-JP', 'ar-EG'];
const SAMPLE_DATE = new Date('2026-04-07T16:45:00Z');
const DATE_TIME_PART_OPTIONS = {
  weekday: ['none', 'narrow', 'short', 'long'],
  era: ['none', 'narrow', 'short', 'long'],
  year: ['none', 'numeric', '2-digit'],
  month: ['none', 'numeric', '2-digit', 'narrow', 'short', 'long'],
  day: ['none', 'numeric', '2-digit'],
  hour: ['none', 'numeric', '2-digit'],
  minute: ['none', 'numeric', '2-digit'],
  second: ['none', 'numeric', '2-digit'],
};

function withDefaultLocale(locales = DEFAULT_COMPARISON_LOCALES) {
  return ['undefined', ...locales.filter((locale) => locale !== 'undefined')];
}

function resolveLocale(locale) {
  return locale === 'undefined' ? undefined : locale;
}

function safeFormat(fn) {
  try {
    return fn();
  } catch (error) {
    return `Error: ${error.message}`;
  }
}

function serializeValue(value) {
  if (typeof value === 'string') return JSON.stringify(value);
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value instanceof Date) return `new Date(${JSON.stringify(value.toISOString())})`;
  return JSON.stringify(value, null, 2);
}

function optionsToCode(options) {
  const entries = Object.entries(options).filter(([, value]) => value !== undefined);
  if (entries.length === 0) return '{}';
  return `{
${entries.map(([key, value]) => `  ${key}: ${serializeValue(value)}`).join(',\n')}
}`;
}

function buildCode({ locale, constructorName, options, expression, formatterName = 'formatter' }) {
  const localeCode = locale === 'undefined' ? 'undefined' : JSON.stringify(locale);
  return `const ${formatterName} = new Intl.${constructorName}(${localeCode}, ${optionsToCode(options || {})});
${expression(formatterName)};`;
}

function buildFormatCode({ locale, constructorName, options, value }) {
  return buildCode({
    locale,
    constructorName,
    options,
    expression: (name) => `${name}.format(${serializeValue(value)})`,
  });
}

function localeLabel(locale) {
  if (locale === 'undefined') return 'Your locale';
  return LOCALES.find((item) => item.value === locale)?.label ?? locale;
}

function CodeExample({ code }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    Prism.highlightAll();
  }, [code]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className={styles.codeExample}>
      <div className={styles.codeBlockWrap}>
        <button type="button" className={styles.copyButton} onClick={handleCopy}>
          {copied ? 'Copied 🎉' : 'Copy JS'}
        </button>
        <div className="gatsby-highlight">
          <pre className="language-js">
            <code>{code}</code>
          </pre>
        </div>
      </div>
    </div>
  );
}

function ComparisonCard({ locale, result, children, status }) {
  return (
    <div className={styles.comparisonItem}>
      <div className={styles.comparisonHeader}>
        <div className={styles.comparisonLocale}>{localeLabel(locale)}</div>
        <div className={styles.comparisonStatus}>{status ?? (locale === 'undefined' ? undefined : locale)}</div>
      </div>
      {result !== undefined && <div className={styles.comparisonValue}>{result}</div>}
      {children}
    </div>
  );
}

function ComparisonGrid({ items, fullWidth = false }) {
  return (
    <div className={styles.output}>
      <div className={`${styles.comparison} ${fullWidth ? styles.comparisonFullWidth : ''}`}>{items}</div>
    </div>
  );
}

function LocaleCards({ locales = DEFAULT_COMPARISON_LOCALES, defaultCode, renderCard }) {
  const allLocales = withDefaultLocale(locales);

  return (
    <>
      <CodeExample code={defaultCode} />
      <ComparisonGrid items={allLocales.map((locale) => renderCard(locale))} />
    </>
  );
}

export const DateTimeFormatExample = () => {
  const [dateStyle, setDateStyle] = useState('full');
  const [timeStyle, setTimeStyle] = useState('short');
  const [hour12, setHour12] = useState('default');
  const [timeZone, setTimeZone] = useState('UTC');
  const options = {
    dateStyle: dateStyle === 'none' ? undefined : dateStyle,
    timeStyle: timeStyle === 'none' ? undefined : timeStyle,
    hour12: hour12 === 'default' ? undefined : hour12 === 'true',
    timeZone,
  };

  return (
    <div className={styles.demo}>
      <div className={styles.controls}>
        <div className={styles.field}>
          <label>dateStyle</label>
          <select value={dateStyle} onChange={(e) => setDateStyle(e.target.value)}>
            <option value="none">(none)</option>
            <option value="full">full</option>
            <option value="long">long</option>
            <option value="medium">medium</option>
            <option value="short">short</option>
          </select>
        </div>
        <div className={styles.field}>
          <label>timeStyle</label>
          <select value={timeStyle} onChange={(e) => setTimeStyle(e.target.value)}>
            <option value="none">(none)</option>
            <option value="full">full</option>
            <option value="long">long</option>
            <option value="medium">medium</option>
            <option value="short">short</option>
          </select>
        </div>
        <div className={styles.field}>
          <label>hour12</label>
          <select value={hour12} onChange={(e) => setHour12(e.target.value)}>
            <option value="default">locale default</option>
            <option value="true">true</option>
            <option value="false">false</option>
          </select>
        </div>
        <div className={styles.field}>
          <label>timeZone</label>
          <select value={timeZone} onChange={(e) => setTimeZone(e.target.value)}>
            <option value="UTC">UTC</option>
            <option value="Europe/Amsterdam">Europe/Amsterdam</option>
            <option value="America/New_York">America/New_York</option>
            <option value="Asia/Tokyo">Asia/Tokyo</option>
          </select>
        </div>
      </div>
      <LocaleCards
        defaultCode={buildFormatCode({
          locale: undefined,
          constructorName: 'DateTimeFormat',
          options,
          value: SAMPLE_DATE,
        })}
        renderCard={(locale) => (
          <ComparisonCard
            key={locale}
            locale={locale}
            result={safeFormat(() => new Intl.DateTimeFormat(resolveLocale(locale), options).format(SAMPLE_DATE))}
          />
        )}
      />
    </div>
  );
};

export const DateTimeFormatPartsExample = () => {
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [weekday, setWeekday] = useState('long');
  const [era, setEra] = useState('none');
  const [year, setYear] = useState('numeric');
  const [month, setMonth] = useState('long');
  const [day, setDay] = useState('numeric');
  const [hour, setHour] = useState('numeric');
  const [minute, setMinute] = useState('2-digit');
  const [second, setSecond] = useState('2-digit');

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCurrentDate(new Date());
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  const options = {
    weekday: weekday === 'none' ? undefined : weekday,
    day: day === 'none' ? undefined : day,
    hour: hour === 'none' ? undefined : hour,
    minute: minute === 'none' ? undefined : minute,
    second: second === 'none' ? undefined : second,
    month: month === 'none' ? undefined : month,
    year: year === 'none' ? undefined : year,
    era: era === 'none' ? undefined : era,
  };

  return (
    <div className={styles.demo}>
      <div className={styles.controls}>
        <div className={styles.field}>
          <label>weekday</label>
          <select value={weekday} onChange={(e) => setWeekday(e.target.value)}>
            {DATE_TIME_PART_OPTIONS.weekday.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.field}>
          <label>day</label>
          <select value={day} onChange={(e) => setDay(e.target.value)}>
            {DATE_TIME_PART_OPTIONS.day.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.field}>
          <label>hour</label>
          <select value={hour} onChange={(e) => setHour(e.target.value)}>
            {DATE_TIME_PART_OPTIONS.hour.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.field}>
          <label>minute</label>
          <select value={minute} onChange={(e) => setMinute(e.target.value)}>
            {DATE_TIME_PART_OPTIONS.minute.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.field}>
          <label>second</label>
          <select value={second} onChange={(e) => setSecond(e.target.value)}>
            {DATE_TIME_PART_OPTIONS.second.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.field}>
          <label>month</label>
          <select value={month} onChange={(e) => setMonth(e.target.value)}>
            {DATE_TIME_PART_OPTIONS.month.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.field}>
          <label>year</label>
          <select value={year} onChange={(e) => setYear(e.target.value)}>
            {DATE_TIME_PART_OPTIONS.year.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.field}>
          <label>era</label>
          <select value={era} onChange={(e) => setEra(e.target.value)}>
            {DATE_TIME_PART_OPTIONS.era.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>
      <LocaleCards
        locales={[]}
        defaultCode={buildCode({
          locale: undefined,
          constructorName: 'DateTimeFormat',
          options,
          expression: (name) => `${name}.format(new Date())`,
        })}
        renderCard={(locale) => (
          <ComparisonCard
            key={locale}
            locale={locale}
            result={safeFormat(() => new Intl.DateTimeFormat(resolveLocale(locale), options).format(currentDate))}
          />
        )}
      />
    </div>
  );
};

export const DateTimeLocaleComparisonExample = () => {
  const [hour12, setHour12] = useState('default');
  const [timeZone, setTimeZone] = useState('UTC');
  const options = {
    dateStyle: 'full',
    timeStyle: 'short',
    hour12: hour12 === 'default' ? undefined : hour12 === 'true',
    timeZone,
  };

  return (
    <div className={styles.demo}>
      <div className={styles.controls}>
        <div className={styles.field}>
          <label>hour12</label>
          <select value={hour12} onChange={(e) => setHour12(e.target.value)}>
            <option value="default">locale default</option>
            <option value="true">true</option>
            <option value="false">false</option>
          </select>
        </div>
        <div className={styles.field}>
          <label>timeZone</label>
          <select value={timeZone} onChange={(e) => setTimeZone(e.target.value)}>
            <option value="UTC">UTC</option>
            <option value="Europe/Amsterdam">Europe/Amsterdam</option>
            <option value="America/New_York">America/New_York</option>
            <option value="Asia/Tokyo">Asia/Tokyo</option>
          </select>
        </div>
      </div>
      <LocaleCards
        defaultCode={buildFormatCode({
          locale: undefined,
          constructorName: 'DateTimeFormat',
          options,
          value: SAMPLE_DATE,
        })}
        renderCard={(locale) => (
          <ComparisonCard
            key={locale}
            locale={locale}
            result={safeFormat(() => new Intl.DateTimeFormat(resolveLocale(locale), options).format(SAMPLE_DATE))}
          />
        )}
      />
    </div>
  );
};

export const RelativeTimeFormatExample = () => {
  const [value, setValue] = useState(-3);
  const [unit, setUnit] = useState('day');
  const [numeric, setNumeric] = useState('auto');
  const options = { numeric };
  const numericValue = Number(value);

  return (
    <div className={styles.demo}>
      <div className={styles.controls}>
        <div className={styles.field}>
          <label>Value (negative = past)</label>
          <input type="number" value={value} onChange={(e) => setValue(e.target.value)} />
        </div>
        <div className={styles.field}>
          <label>Unit</label>
          <select value={unit} onChange={(e) => setUnit(e.target.value)}>
            {['second', 'minute', 'hour', 'day', 'week', 'month', 'quarter', 'year'].map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.field}>
          <label>numeric</label>
          <select value={numeric} onChange={(e) => setNumeric(e.target.value)}>
            <option value="auto">auto</option>
            <option value="always">always</option>
          </select>
        </div>
      </div>
      <LocaleCards
        defaultCode={buildCode({
          locale: undefined,
          constructorName: 'RelativeTimeFormat',
          options,
          expression: (name) => `${name}.format(${numericValue}, ${JSON.stringify(unit)})`,
        })}
        renderCard={(locale) => (
          <ComparisonCard
            key={locale}
            locale={locale}
            result={safeFormat(() =>
              new Intl.RelativeTimeFormat(resolveLocale(locale), options).format(numericValue, unit)
            )}
          />
        )}
      />
    </div>
  );
};

export const DurationFormatExample = () => {
  const [style, setStyle] = useState('long');
  const [hours, setHours] = useState(2);
  const [minutes, setMinutes] = useState(45);
  const [seconds, setSeconds] = useState(30);

  const duration = useMemo(() => {
    const result = {};
    if (Number(hours) !== 0) result.hours = Number(hours);
    if (Number(minutes) !== 0) result.minutes = Number(minutes);
    if (Number(seconds) !== 0) result.seconds = Number(seconds);
    return result;
  }, [hours, minutes, seconds]);

  const options = { style };

  return (
    <div className={styles.demo}>
      <div className={styles.controls}>
        <div className={styles.field}>
          <label>style</label>
          <select value={style} onChange={(e) => setStyle(e.target.value)}>
            <option value="long">long</option>
            <option value="short">short</option>
            <option value="narrow">narrow</option>
            <option value="digital">digital</option>
          </select>
        </div>
        <div className={styles.field}>
          <label>Hours</label>
          <input type="number" min="0" value={hours} onChange={(e) => setHours(e.target.value)} />
        </div>
        <div className={styles.field}>
          <label>Minutes</label>
          <input type="number" min="0" max="59" value={minutes} onChange={(e) => setMinutes(e.target.value)} />
        </div>
        <div className={styles.field}>
          <label>Seconds</label>
          <input type="number" min="0" max="59" value={seconds} onChange={(e) => setSeconds(e.target.value)} />
        </div>
      </div>
      <LocaleCards
        defaultCode={buildFormatCode({
          locale: undefined,
          constructorName: 'DurationFormat',
          options,
          value: duration,
        })}
        renderCard={(locale) => (
          <ComparisonCard
            key={locale}
            locale={locale}
            result={safeFormat(() => new Intl.DurationFormat(resolveLocale(locale), options).format(duration))}
          />
        )}
      />
    </div>
  );
};

export const NumberFormatExample = () => {
  const [value, setValue] = useState(1234567.89);
  const [style, setStyle] = useState('decimal');
  const [notation, setNotation] = useState('standard');
  const [useGrouping, setUseGrouping] = useState('auto');

  const options = {
    style,
    notation,
    useGrouping: useGrouping === 'false' ? false : useGrouping,
  };

  return (
    <div className={styles.demo}>
      <div className={styles.controls}>
        <div className={styles.field}>
          <label>Number</label>
          <input type="number" value={value} onChange={(e) => setValue(e.target.value)} />
        </div>
        <div className={styles.field}>
          <label>style</label>
          <select value={style} onChange={(e) => setStyle(e.target.value)}>
            <option value="decimal">decimal</option>
            <option value="percent">percent</option>
          </select>
        </div>
        <div className={styles.field}>
          <label>notation</label>
          <select value={notation} onChange={(e) => setNotation(e.target.value)}>
            <option value="standard">standard</option>
            <option value="compact">compact</option>
            <option value="scientific">scientific</option>
            <option value="engineering">engineering</option>
          </select>
        </div>
        <div className={styles.field}>
          <label>useGrouping</label>
          <select value={useGrouping} onChange={(e) => setUseGrouping(e.target.value)}>
            <option value="auto">auto</option>
            <option value="always">always</option>
            <option value="min2">min2</option>
            <option value="false">false</option>
          </select>
        </div>
      </div>
      <LocaleCards
        defaultCode={buildFormatCode({
          locale: undefined,
          constructorName: 'NumberFormat',
          options,
          value: Number(value),
        })}
        renderCard={(locale) => (
          <ComparisonCard
            key={locale}
            locale={locale}
            result={safeFormat(() => new Intl.NumberFormat(resolveLocale(locale), options).format(Number(value)))}
          />
        )}
      />
    </div>
  );
};

export const CurrencyLocaleComparisonExample = () => {
  const [amount, setAmount] = useState(123456.789);
  const [currency, setCurrency] = useState('EUR');
  const [currencyDisplay, setCurrencyDisplay] = useState('symbol');

  const options = {
    style: 'currency',
    currency,
    currencyDisplay,
  };

  return (
    <div className={styles.demo}>
      <div className={styles.controls}>
        <div className={styles.field}>
          <label>Amount</label>
          <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </div>
        <div className={styles.field}>
          <label>Currency</label>
          <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
            {['USD', 'EUR', 'GBP', 'JPY', 'CNY', 'BRL'].map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.field}>
          <label>currencyDisplay</label>
          <select value={currencyDisplay} onChange={(e) => setCurrencyDisplay(e.target.value)}>
            <option value="symbol">symbol</option>
            <option value="narrowSymbol">narrowSymbol</option>
            <option value="code">code</option>
            <option value="name">name</option>
          </select>
        </div>
      </div>
      <LocaleCards
        defaultCode={buildFormatCode({
          locale: undefined,
          constructorName: 'NumberFormat',
          options,
          value: Number(amount),
        })}
        renderCard={(locale) => (
          <ComparisonCard
            key={locale}
            locale={locale}
            result={safeFormat(() => new Intl.NumberFormat(resolveLocale(locale), options).format(Number(amount)))}
          />
        )}
      />
    </div>
  );
};

export const UnitFormatExample = () => {
  const [value, setValue] = useState(100);
  const [unit, setUnit] = useState('kilometer-per-hour');
  const [unitDisplay, setUnitDisplay] = useState('short');

  const units = [
    'kilometer',
    'mile',
    'meter',
    'foot',
    'kilometer-per-hour',
    'mile-per-hour',
    'liter',
    'gallon',
    'gram',
    'kilogram',
    'pound',
    'celsius',
    'fahrenheit',
    'year',
    'month',
    'day',
    'hour',
    'minute',
    'second',
    'gigabyte',
    'megabyte',
    'kilobyte',
    'byte',
    'megabit-per-second',
  ];

  const options = {
    style: 'unit',
    unit,
    unitDisplay,
  };

  return (
    <div className={styles.demo}>
      <div className={styles.controls}>
        <div className={styles.field}>
          <label>Value</label>
          <input type="number" value={value} onChange={(e) => setValue(e.target.value)} />
        </div>
        <div className={styles.field}>
          <label>Unit</label>
          <select value={unit} onChange={(e) => setUnit(e.target.value)}>
            {units.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.field}>
          <label>unitDisplay</label>
          <select value={unitDisplay} onChange={(e) => setUnitDisplay(e.target.value)}>
            <option value="short">short</option>
            <option value="long">long</option>
            <option value="narrow">narrow</option>
          </select>
        </div>
      </div>
      <LocaleCards
        defaultCode={buildFormatCode({
          locale: undefined,
          constructorName: 'NumberFormat',
          options,
          value: Number(value),
        })}
        renderCard={(locale) => (
          <ComparisonCard
            key={locale}
            locale={locale}
            result={safeFormat(() => new Intl.NumberFormat(resolveLocale(locale), options).format(Number(value)))}
          />
        )}
      />
    </div>
  );
};

export const ListFormatExample = () => {
  const [type, setType] = useState('conjunction');
  const [listStyle, setListStyle] = useState('long');
  const [items, setItems] = useState('Apples\nOranges\nBananas');
  const itemArray = items
    .split('\n')
    .map((value) => value.trim())
    .filter(Boolean);
  const options = { type, style: listStyle };

  return (
    <div className={styles.demo}>
      <div className={styles.controls}>
        <div className={styles.field}>
          <label>type</label>
          <select value={type} onChange={(e) => setType(e.target.value)}>
            <option value="conjunction">conjunction</option>
            <option value="disjunction">disjunction</option>
            <option value="unit">unit</option>
          </select>
        </div>
        <div className={styles.field}>
          <label>style</label>
          <select value={listStyle} onChange={(e) => setListStyle(e.target.value)}>
            <option value="long">long</option>
            <option value="short">short</option>
            <option value="narrow">narrow</option>
          </select>
        </div>
        <div className={styles.field} style={{ flex: '2 1 220px' }}>
          <label>Items (one per line)</label>
          <textarea value={items} onChange={(e) => setItems(e.target.value)} />
        </div>
      </div>
      <LocaleCards
        defaultCode={buildFormatCode({ locale: undefined, constructorName: 'ListFormat', options, value: itemArray })}
        locales={['en-US', 'en-GB', 'nl-NL', 'fr-FR', 'es-ES']}
        renderCard={(locale) => (
          <ComparisonCard
            key={locale}
            locale={locale}
            result={safeFormat(() => new Intl.ListFormat(resolveLocale(locale), options).format(itemArray))}
          />
        )}
      />
    </div>
  );
};

export const PluralRulesExample = () => {
  const [type, setType] = useState('cardinal');
  const [value, setValue] = useState(1);
  const options = { type };
  const numericValue = Number(value);

  const pluralRulesCode =
    type === 'cardinal'
      ? `const count = ${numericValue};
const rules = new Intl.PluralRules(undefined, { type: 'cardinal' });
rules.select(count);`
      : `const rank = ${numericValue};
const rules = new Intl.PluralRules(undefined, { type: 'ordinal' });
rules.select(rank);`;

  return (
    <div className={styles.demo}>
      <div className={styles.controls}>
        <div className={styles.field}>
          <label>type</label>
          <select value={type} onChange={(e) => setType(e.target.value)}>
            <option value="cardinal">cardinal</option>
            <option value="ordinal">ordinal</option>
          </select>
        </div>
        <div className={styles.field}>
          <label>Number</label>
          <input type="number" value={value} onChange={(e) => setValue(e.target.value)} />
        </div>
      </div>
      <LocaleCards
        defaultCode={pluralRulesCode}
        locales={['en-US', 'nl-NL', 'fr-FR', 'ja-JP', 'ar-EG']}
        renderCard={(locale) => (
          <ComparisonCard
            key={locale}
            locale={locale}
            result={safeFormat(() => new Intl.PluralRules(resolveLocale(locale), options).select(numericValue))}
          />
        )}
      />
    </div>
  );
};

function segmentInfo(locale, granularity, text) {
  const segmenter = new Intl.Segmenter(locale, { granularity });
  const segments = Array.from(segmenter.segment(text));
  const count = granularity === 'word' ? segments.filter((segment) => segment.isWordLike).length : segments.length;
  return { segments, count };
}

function segmentCode(granularity) {
  return `const segmenter = new Intl.Segmenter('default', {
  granularity: ${JSON.stringify(granularity)}
});
const segments = Array.from(segmenter.segment(text));`;
}

export const SegmenterExample = () => {
  const [granularity, setGranularity] = useState('word');
  const [graphemeText, setGraphemeText] = useState('👨‍👩‍👧‍👦');
  const [sentenceText, setSentenceText] = useState(
    "This article could be 100.000 words long if you don't look out. Do you really need that many examples?"
  );
  const wordExamples = [
    { locale: 'en-US', text: 'How many words are in this sentence?' },
    { locale: 'nl-NL', text: 'Hoeveel woorden zitten er in deze zin?' },
    { locale: 'ja-JP', text: 'この文には何語ありますか？' },
    { locale: 'ar-EG', text: 'كم عدد الكلمات في هذه الجملة؟' },
  ];

  const getTagClass = (segment) => {
    if (granularity === 'word') {
      if (segment.isWordLike) return `${styles.tag} ${styles.tagWord}`;
      if (/^\s+$/.test(segment.segment)) return `${styles.tag} ${styles.tagSpace}`;
      return `${styles.tag} ${styles.tagPunct}`;
    }
    return styles.tag;
  };

  return (
    <div className={styles.demo}>
      <div className={styles.controls}>
        <div className={styles.field}>
          <label>granularity</label>
          <select value={granularity} onChange={(e) => setGranularity(e.target.value)}>
            <option value="grapheme">grapheme</option>
            <option value="word">word</option>
            <option value="sentence">sentence</option>
          </select>
        </div>
        {granularity === 'grapheme' && (
          <div className={styles.field} style={{ flex: '2 1 320px' }}>
            <label>Text</label>
            <input type="text" value={graphemeText} onChange={(e) => setGraphemeText(e.target.value)} />
          </div>
        )}
        {granularity === 'sentence' && (
          <div className={styles.field} style={{ flex: '2 1 320px' }}>
            <label>Text</label>
            <textarea value={sentenceText} onChange={(e) => setSentenceText(e.target.value)} />
          </div>
        )}
      </div>
      <CodeExample code={segmentCode(granularity)} />
      {granularity === 'word' && (
        <>
          <ComparisonGrid
            fullWidth
            items={wordExamples.map(({ locale, text }) => {
              const { segments, count } = segmentInfo(locale, granularity, text);
              return (
                <ComparisonCard key={locale} locale={locale}>
                  <div className={styles.comparisonSource}>{text}</div>
                  <div className={styles.comparisonValueSmall}>{`${count} word-like segments`}</div>
                  <div className={styles.tagList}>
                    {segments.map((segment) => (
                      <span key={`${locale}-${segment.index}-${segment.segment}`} className={getTagClass(segment)}>
                        {segment.segment === ' ' ? '·' : segment.segment}
                      </span>
                    ))}
                  </div>
                </ComparisonCard>
              );
            })}
          />
          <div className={styles.legend}>
            <span>
              <strong style={{ color: 'dodgerblue' }}>Blue</strong> = word-like
            </span>
            <span>
              <strong style={{ color: '#888' }}>Gray dashed</strong> = whitespace
            </span>
            <span>
              <strong style={{ color: '#ff86c6' }}>Pink</strong> = punctuation
            </span>
          </div>
        </>
      )}
      {granularity === 'grapheme' &&
        (() => {
          const { segments: emojiSegments, count: emojiCount } = segmentInfo(undefined, granularity, graphemeText);
          return (
            <ComparisonGrid
              items={[
                <ComparisonCard key="default-grapheme" locale="default">
                  <div className={styles.comparisonSource}>{graphemeText}</div>
                  <div
                    className={styles.comparisonValueSmall}
                  >{`${emojiCount} grapheme segment${emojiCount === 1 ? '' : 's'}`}</div>
                  <div className={styles.tagList}>
                    {emojiSegments.map((segment) => (
                      <span
                        key={`grapheme-${segment.index}-${segment.segment}`}
                        className={`${styles.tag} ${styles.tagWord}`}
                      >
                        {segment.segment}
                      </span>
                    ))}
                  </div>
                </ComparisonCard>,
              ]}
            />
          );
        })()}
      {granularity === 'sentence' &&
        (() => {
          const { segments, count } = segmentInfo(undefined, granularity, sentenceText);
          return (
            <ComparisonGrid
              fullWidth
              items={[
                <ComparisonCard key="default-sentence" locale="undefined">
                  <div className={styles.comparisonSource}>{sentenceText}</div>
                  <div
                    className={styles.comparisonValueSmall}
                  >{`${count} sentence segment${count === 1 ? '' : 's'}`}</div>
                  <div className={styles.tagList}>
                    {segments.map((segment) => (
                      <span
                        key={`sentence-${segment.index}-${segment.segment}`}
                        className={`${styles.tag} ${styles.tagWord}`}
                      >
                        {segment.segment}
                      </span>
                    ))}
                  </div>
                </ComparisonCard>,
              ]}
            />
          );
        })()}
    </div>
  );
};

function collatorCode(locale, sensitivity, items) {
  const localeCode = locale === 'default' ? `undefined` : JSON.stringify(locale);
  return `const items = ${JSON.stringify(items)};
const collator = new Intl.Collator(${localeCode}, {
  sensitivity: ${JSON.stringify(sensitivity)}
});
items.sort(collator.compare);`;
}

export const CollatorExample = () => {
  const [items, setItems] = useState('ñ n o á A ß z');
  const [sensitivity, setSensitivity] = useState('base');
  const itemArray = items
    .split(/\s+/)
    .map((value) => value.trim())
    .filter(Boolean);
  const locales = ['en-US', 'es-ES', 'de-DE', 'sv-SE'];
  const plainSorted = [...itemArray].sort();

  return (
    <div className={styles.demo}>
      <div className={styles.controls}>
        <div className={styles.field}>
          <label>sensitivity</label>
          <select value={sensitivity} onChange={(e) => setSensitivity(e.target.value)}>
            <option value="base">base</option>
            <option value="accent">accent</option>
            <option value="case">case</option>
            <option value="variant">variant</option>
          </select>
        </div>
        <div className={styles.field} style={{ flex: '2 1 150px' }}>
          <label>Items (space separated)</label>
          <input type="text" value={items} onChange={(e) => setItems(e.target.value)} />
        </div>
      </div>
      <CodeExample code={collatorCode(undefined, sensitivity, itemArray)} />
      <ComparisonGrid
        items={[
          <ComparisonCard key="plain-sort" locale="array.sort()" status="plain sort">
            <div className={styles.tagList}>
              {plainSorted.map((item) => (
                <span key={`plain-sort-${item}`} className={`${styles.tag} ${styles.tagWord}`}>
                  {item}
                </span>
              ))}
            </div>
          </ComparisonCard>,
          ...withDefaultLocale(locales).map((locale) => {
            const collator = new Intl.Collator(resolveLocale(locale), { sensitivity });
            const sorted = [...itemArray].sort(collator.compare);
            return (
              <ComparisonCard key={locale} locale={locale}>
                <div className={styles.tagList}>
                  {sorted.map((item) => (
                    <span key={`${locale}-${item}`} className={`${styles.tag} ${styles.tagWord}`}>
                      {item}
                    </span>
                  ))}
                </div>
              </ComparisonCard>
            );
          }),
        ]}
      />
    </div>
  );
};
